import type {
	AdditionalFeeItemInput,
	AdditionalFeeKidsPriceObjectInput,
	HotelPolicyInput,
	QuestionItemInput,
} from "@/lib/gql/__generated__/graphql";

import type { AccommodationHotelPolicyFormModel } from "../../../type";

type Props = {
	viewModel: AccommodationHotelPolicyFormModel["ViewModel"];
};

export const viewModelToHotelPolicyInputConverter = ({ viewModel }: Props): HotelPolicyInput => {
	const hotelPolicyInput: HotelPolicyInput = {
		id: viewModel.id,

		amenities: viewModel.amenities.value.map((amenity) => amenity.value),
		facilities: viewModel.facilities.value.map((facility) => facility.value),

		calcAdultPriceAsTotalNum: viewModel.calcAdultPriceAsTotalNum.value,
		cancelPolicy: viewModel.cancelPolicy.value.map((item) => {
			return {
				beforeDays: item.beforeDays.value,
				chargeRatio: item.chargeRatio.value,
			} satisfies HotelPolicyInput["cancelPolicy"][0];
		}),

		checkInEnd: viewModel.checkInEnd.value,
		checkInStart: viewModel.checkInStart.value,
		checkOut: viewModel.checkOut.value,

		hotelInfo: viewModel.hotelInfo.value.map((item) => {
			return {
				description: item.description.value,
				title: item.title.value,
			} satisfies HotelPolicyInput["hotelInfo"][0];
		}),

		kidsPolicy:
			viewModel.kidsPolicy?.value?.map((item) => ({
				kidsDiscountType: item.kidsDiscountType.value,
				kidsMaxAge: item.kidsMaxAge.value,
				ratio: item.ratio?.value,
				static:
					item.static?.amount?.value !== undefined && item.static?.currencyType?.value !== undefined
						? {
								amount: item.static.amount?.value,
								currencyType: item.static.currencyType?.value,
							}
						: undefined,
			})) ?? [],
		kidsCosleepingPolicy: viewModel.kidsCosleepingPolicy?.isActive?.value
			? ({
					isActive: viewModel.kidsCosleepingPolicy?.isActive?.value,
					kidsDiscountType: viewModel.kidsCosleepingPolicy?.kidsDiscountType?.value,
					kidsMaxAge: viewModel.kidsCosleepingPolicy?.kidsMaxAge?.value,
					static:
						viewModel.kidsCosleepingPolicy?.static?.amount?.value !== null &&
						viewModel.kidsCosleepingPolicy?.static?.amount?.value !== undefined
							? {
									amount: viewModel.kidsCosleepingPolicy.static.amount.value,
									currencyType: viewModel.kidsCosleepingPolicy.static.currencyType.value,
								}
							: undefined,
					ratio: viewModel.kidsCosleepingPolicy.ratio?.value,
				} satisfies HotelPolicyInput["kidsCosleepingPolicy"])
			: undefined,
		additionalFees: viewModel.additionalFees.value.map((item) => {
			return {
				additionalID: item.additionalID.value,
				name: item.name.value,
				price: {
					// eslint-disable-next-line @typescript-eslint/no-non-null-asserted-optional-chain
					amount: item.price.amount?.value,
					currencyType: item.price.currencyType.value,
				},
				billingTypeTarget: item.billingTypeTarget.value,
				billingTypeTerm: item.billingTypeTerm.value,
				billingTypeTermValidation: item.billingTypeTermValidation?.value,
				isExcludeCancelFeeTarget: item.isExcludeCancelFeeTarget?.value,
				isExcludedConsumptionTax: item.isExcludedConsumptionTax?.value,
				isNotDiscountTarget: item.isNotDiscountTarget?.value,
				isNotTargetOnDayTrip: item.isNotTargetOnDayTrip?.value,
				kidsPrices: item.kidsPrices?.value?.map((item) => {
					return {
						kidsMaxAge: item.kidsMaxAge.value,
						price: {
							amount: item.price.amount.value,
							currencyType: item.price.currencyType.value,
						},
					} satisfies AdditionalFeeKidsPriceObjectInput;
				}),
				termValidationApplyEndDt: item.termValidationApplyEndDt?.value,
				termValidationApplyStartDt: item.termValidationApplyStartDt?.value,
				useKidsPrice: item.useKidsPrice?.value,
			} satisfies AdditionalFeeItemInput;
		}),

		baseReservationType: viewModel.baseReservationType?.value,
		defaultInitPeopleNum: viewModel.defaultInitPeopleNum.value,
		defaultPlanID: viewModel.defaultPlanID?.value,
		hideMailMagazineQuestion: viewModel.hideMailMagazineQuestion.value,
		isLocalPaymentAvailable: viewModel.isLocalPaymentAvailable.value,
		isShowRoomPriceInCalender: viewModel.isShowRoomPriceInCalender.value,
		isShowPlanIncludedPrice: viewModel.isShowPlanIncludedPrice.value,
		isShowBestRateNotice: viewModel.isShowBestRateNotice.value,
		isCalendarStartFromAvailableMonth: viewModel.isCalendarStartFromAvailableMonth.value,

		localPaymentCancelFeeTransferInfo: viewModel.localPaymentCancelFeeTransferInfo
			? {
					bankAccountHolderName: viewModel.localPaymentCancelFeeTransferInfo.bankAccountHolderName?.value,
					bankAccountNumber: viewModel.localPaymentCancelFeeTransferInfo.bankAccountNumber?.value,
					bankAccountType: viewModel.localPaymentCancelFeeTransferInfo.bankAccountType?.value,
					bankName: viewModel.localPaymentCancelFeeTransferInfo.bankName?.value,
					branchName: viewModel.localPaymentCancelFeeTransferInfo.branchName?.value,
				}
			: undefined,

		localPaymentCancelFeeTransferLimitDays: viewModel.localPaymentCancelFeeTransferLimitDays.limitDays?.value,

		questions: viewModel.questions.value.map((item) => {
			return {
				options: item.options?.value?.map((item) => item.value),
				question: item.question.value,
				questionID: item.questionID.value,
				questionType: item.questionType.value,
				required: item.required?.value,
			} satisfies QuestionItemInput;
		}),

		requiredPlanSelectForReservation: viewModel.requiredPlanSelectForReservation.value,
		requiredPrepMillisecond: viewModel.requiredPrepMillisecond.value,
	} satisfies HotelPolicyInput;

	return hotelPolicyInput;
};
